'use client';

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, DetailedHTMLProps, FC, ReactNode } from 'react';
import React, { isValidElement } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';
import { LoadingIndicator } from '../loadingIndicator';
import { cx, sortCx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';
import { useRouterLink, type RouterOptions } from '../router';

const SEMANTIC_NAME = 'RomaButton';

export const styles = sortCx({
    common: {
        root: [
            'group relative inline-flex cursor-pointer items-center outline-primary justify-center whitespace-nowrap select-none transition duration-100 ease-linear before:absolute focus-visible:outline-2 focus-visible:-outline-offset-1',
            // Disabled styles
            'disabled:cursor-default disabled:text-fg-disabled',
            // Icon styles
            'disabled:*:data-icon:text-fg-disabled_subtle',
            // Same as `icon` but for SSR icons that cannot be passed to the client as functions.
            '*:data-icon:pointer-events-none *:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:transition-inherit-all',

            /* Special styles when the button is used inside an input wrapper */
            // - focus styles
            'in-data-input-wrapper:focus:!z-50 in-data-input-wrapper:focus-visible:-outline-offset-2',
            // - leading button in input group
            'in-data-input-wrapper:in-data-leading:-mr-px in-data-input-wrapper:in-data-leading:rounded-r-none in-data-input-wrapper:in-data-leading:before:rounded-r-none',
            // - trailing button in input group
            'in-data-input-wrapper:in-data-trailing:-ml-px in-data-input-wrapper:in-data-trailing:rounded-l-none in-data-input-wrapper:in-data-trailing:before:rounded-l-none',
        ].join(' '),
        icon: 'pointer-events-none size-5 shrink-0 transition-inherit-all',
    },
    sizes: {
        sm: {
            root: [
                'h-8 gap-1 rounded-xs px-2.5 py-1.5 text-sm font-semibold before:rounded-[5px]',
                'data-icon-only:size-8 data-icon-only:p-0',
                'in-data-input-wrapper:px-3 in-data-input-wrapper:py-1.5 in-data-input-wrapper:data-icon-only:p-0',
            ].join(' '),
            linkRoot: 'h-auto gap-1 data-icon-only:h-auto data-icon-only:w-auto',
        },
        md: {
            root: [
                'h-10 gap-1 rounded-sm px-3.5 py-2.5 text-md font-semibold before:rounded-[7px]',
                'data-icon-only:size-10 data-icon-only:p-0',
                'in-data-input-wrapper:gap-1.5 in-data-input-wrapper:px-3.5 in-data-input-wrapper:py-2.5 in-data-input-wrapper:text-md in-data-input-wrapper:data-icon-only:p-0',
            ].join(' '),
            linkRoot: 'h-auto gap-1 data-icon-only:h-auto data-icon-only:w-auto',
        },
        lg: {
            root: [
                'h-11 gap-1.5 rounded-sm px-4 py-2.5 text-lg font-semibold before:rounded-[7px]',
                'data-icon-only:size-11 data-icon-only:p-0',
            ].join(' '),
            linkRoot: 'h-auto gap-1.5 data-icon-only:h-auto data-icon-only:w-auto',
        },
        xl: {
            root: [
                'h-12 gap-1.5 rounded-md px-4 py-2.5 text-lg font-semibold before:rounded-[9px]',
                'data-icon-only:size-12 data-icon-only:p-0',
            ].join(' '),
            linkRoot: 'h-auto gap-1.5 data-icon-only:h-auto data-icon-only:w-auto',
        },
    },

    colors: {
        primary: {
            root: [
                'bg-brand-solid text-white ring-1 ring-transparent ring-inset data-loading:bg-brand-solid_hover',
                'hover:bg-brand-solid_hover',
                // Disabled styles
                'disabled:bg-disabled',
                // Icon styles
                '*:data-icon:text-button-primary-icon hover:*:data-icon:text-button-primary-icon_hover',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-white',
            ].join(' '),
        },
        secondary: {
            root: [
                'bg-primary text-secondary ring-1 ring-primary ring-inset hover:text-secondary_hover data-loading:bg-primary_hover',
                // Disabled styles
                'disabled:ring-disabled_subtle',
                // Icon styles
                '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-secondary',
            ].join(' '),
        },
        tertiary: {
            root: [
                'text-tertiary hover:bg-primary_hover hover:text-tertiary_hover data-loading:bg-primary_hover',
                // Icon styles
                '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-tertiary',
            ].join(' '),
        },
        'link-gray': {
            root: [
                'justify-normal rounded p-0! text-tertiary hover:text-tertiary_hover',
                // Inner text underline
                '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
                // Icon styles
                '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-tertiary',
            ].join(' '),
        },
        'link-color': {
            root: [
                'justify-normal rounded p-0! text-brand-primary hover:text-brand-primary_hover',
                // Inner text underline
                '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
                // Icon styles
                '*:data-icon:text-fg-brand-primary hover:*:data-icon:text-fg-brand-primary_hover',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-brand-primary',
            ].join(' '),
        },
        'primary-destructive': {
            root: [
                'bg-error-solid text-white ring-1 ring-transparent outline-error_subtle ring-inset hover:bg-bg-error-solid_hover',
                // Disabled styles
                'disabled:bg-disabled disabled:ring-disabled_subtle',
                // Icon styles
                '*:data-icon:text-button-destructive-primary-icon hover:*:data-icon:text-button-destructive-primary-icon_hover',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-white',
            ].join(' '),
        },
        'secondary-destructive': {
            root: [
                'bg-primary text-error-primary ring-1 ring-error_subtle outline-error_subtle ring-inset hover:bg-error-primary hover:text-error-primary_hover data-loading:bg-error-primary',
                // Disabled styles
                'disabled:bg-primary disabled:ring-disabled_subtle',
                // Icon styles
                '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-error-primary',
            ].join(' '),
        },
        'tertiary-destructive': {
            root: [
                'text-error-primary outline-error_subtle hover:bg-error-primary hover:text-error-primary_hover data-loading:bg-error-primary',
                // Icon styles
                '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-error-primary',
            ].join(' '),
        },
        'link-destructive': {
            root: [
                'justify-normal rounded p-0! text-error-primary outline-error_subtle hover:text-error-primary_hover',
                // Inner text underline
                '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
                // Icon styles
                '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
                // Loading icon styles
                '[&_[data-icon][data-icon=loading]]:text-error-primary',
            ].join(' '),
        },
    },
});

/**
 * Common props shared between button and anchor variants
 */
export interface CommonProps {
    /** Disables the button and shows a disabled state */
    isDisabled?: boolean;
    /** Shows a loading spinner and disables the button */
    isLoading?: boolean;
    /** The size variant of the button */
    size?: keyof typeof styles.sizes;
    /** The color variant of the button */
    color?: keyof typeof styles.colors;
    /** Icon component or element to show before the text */
    iconLeading?: FC<{ className?: string }> | ReactNode;
    /** Icon component or element to show after the text */
    iconTrailing?: FC<{ className?: string }> | ReactNode;
    /** Removes horizontal padding from the text content */
    noTextPadding?: boolean;
    /** When true, keeps the text visible during loading state */
    showTextWhileLoading?: boolean;
}

/**
 * Props for the button variant (non-link)
 */
export interface BaseButtonProps
    extends
        CommonProps,
        DetailedHTMLProps<Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'slot'>, HTMLButtonElement> {
    /** Slot name for react-aria component */
    slot?: AriaButtonProps['slot'];
}

/**
 * Props for the link variant (anchor tag)
 */
export interface BaseLinkProps
    extends CommonProps, DetailedHTMLProps<Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>, HTMLAnchorElement> {
    /** Options applied by the configured client-side router. */
    routerOptions?: RouterOptions;
}

/** Union type of button and link props */
export type ButtonProps = BaseButtonProps | BaseLinkProps;

export type ButtonContentProps = Pick<
    CommonProps,
    'iconLeading' | 'iconTrailing' | 'noTextPadding' | 'isLoading' | 'showTextWhileLoading'
> & {
    children?: ReactNode;
};

const LINK_COLORS = new Set(['link-gray', 'link-color', 'link-destructive']);

/** Returns true when the button's text span should have no horizontal padding. Link colors and explicit opt-out both suppress it. */
export const resolveNoTextPadding = (
    color: NonNullable<CommonProps['color']>,
    noTextPadding: CommonProps['noTextPadding'],
): boolean => LINK_COLORS.has(color) || Boolean(noTextPadding);

/** Renders a button icon from either a React element or a component reference. */
export const renderIcon = (Icon: FC<{ className?: string }> | ReactNode, placement: 'leading' | 'trailing') => {
    if (isValidElement(Icon)) return Icon;
    if (isReactComponent(Icon)) return <Icon data-icon={placement} className={styles.common.icon} />;
    return null;
};

/** Renders the inner content of a button: leading icon, optional loading spinner, text, and trailing icon. */
export const ButtonContent: React.FC<ButtonContentProps> = ({
    iconLeading,
    iconTrailing,
    children,
    noTextPadding,
    isLoading,
    showTextWhileLoading,
}) => (
    <>
        {renderIcon(iconLeading, 'leading')}

        {isLoading && (
            <LoadingIndicator.Spinner
                size="sm"
                className={cx(
                    styles.common.icon,
                    !showTextWhileLoading && 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                )}
            />
        )}

        {children != null && (
            <span data-text className={cx('transition-inherit-all', !noTextPadding && 'px-0.5')}>
                {children}
            </span>
        )}

        {renderIcon(iconTrailing, 'trailing')}
    </>
);

ButtonContent.displayName = 'ButtonContent';

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
    const {
        size = 'sm',
        color = 'primary',
        type = 'button',
        children,
        className,
        noTextPadding,
        iconLeading: IconLeading,
        iconTrailing: IconTrailing,
        isDisabled: disabled,
        isLoading: loading,
        showTextWhileLoading,
        ...otherProps
    } = props;

    const href = 'href' in otherProps ? otherProps.href : undefined;
    const { routerOptions, ...componentProps } = otherProps as typeof otherProps & Pick<BaseLinkProps, 'routerOptions'>;
    const { href: resolvedHref, render } = useRouterLink(href, routerOptions);

    const ariaLinkProps = componentProps as Omit<
        React.ComponentPropsWithoutRef<typeof AriaLink>,
        'children' | 'className'
    >;
    const ariaButtonProps = componentProps as Omit<
        React.ComponentPropsWithoutRef<typeof AriaButton>,
        'children' | 'className' | 'type'
    >;
    const buttonType = type as BaseButtonProps['type'];

    const isIcon = (IconLeading || IconTrailing) && !children;
    const isLinkType = LINK_COLORS.has(color);
    const resolvedNoTextPadding = resolveNoTextPadding(color, noTextPadding);

    const content = (
        <ButtonContent
            iconLeading={IconLeading}
            iconTrailing={IconTrailing}
            noTextPadding={resolvedNoTextPadding}
            isLoading={loading}
            showTextWhileLoading={showTextWhileLoading}
        >
            {children}
        </ButtonContent>
    );

    const commonProps = {
        ref,
        'data-loading': loading ? true : undefined,
        'data-icon-only': isIcon ? true : undefined,
        className: cx(
            SEMANTIC_NAME,
            styles.common.root,
            styles.sizes[size].root,
            styles.colors[color].root,
            isLinkType && styles.sizes[size].linkRoot,
            (loading || (href && (disabled || loading))) && 'pointer-events-none',
            // If in `loading` state, hide everything except the loading icon (and text if `showTextWhileLoading` is true).
            loading &&
                (showTextWhileLoading
                    ? '[&>*:not([data-icon=loading]):not([data-text])]:hidden'
                    : '[&>*:not([data-icon=loading])]:invisible'),
            className,
        ),
    };

    if (href) {
        return (
            <AriaLink
                {...commonProps}
                {...ariaLinkProps}
                ref={ref as React.Ref<HTMLAnchorElement>}
                href={disabled ? undefined : resolvedHref}
                isDisabled={disabled}
                render={render}
            >
                {content}
            </AriaLink>
        );
    }

    return (
        <AriaButton
            {...commonProps}
            {...ariaButtonProps}
            ref={ref as React.Ref<HTMLButtonElement>}
            type={buttonType}
            isPending={loading}
            isDisabled={disabled}
        >
            {content}
        </AriaButton>
    );
});

Button.displayName = SEMANTIC_NAME;
